import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  Button,
  TextareaAutosize,
  MenuItem,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import "../../styles/inputBox.css";
import Select from "@mui/material/Select";
import { useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { industryServiceData } from "../../utils/industryServiceData";
import ProfileStepDots from "./ProfileStepDots";

interface ProfileSecondProps {
  onBack: () => void;
  onNext: () => void;
  setProfileData: (data: any) => void;
  initialData: any;
  mode: any;
  currentPage?: number;
  onGoToPage?: (page: number) => void;
}

const ProfileSecond: React.FC<ProfileSecondProps> = ({
  onBack,
  onNext,
  setProfileData,
  initialData,
  currentPage = 2,
  onGoToPage,
}: ProfileSecondProps) => {
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:578px)");
  const [companyVisionAndMission, setCompanyVisionAndMission] = useState("");
  const [employees, setEmployees] = useState("");
  const [industry, setIndustry] = useState("");
  const [services, setServices] = useState("");

  const [errors, setErrors] = useState({
    companyVisionAndMission: "",
    employees: "",
    industry: "",
    services: "",
  });

  const handleBack = () => {
    syncToParent();
    onBack();
  };

  const buildStepData = () => ({
    companyVisionAndMission,
    employees,
    industry,
    services,
  });

  const syncToParent = () => {
    setProfileData({ ...initialData, ...buildStepData() });
  };

  const handleStepNavigate = (page: number) => {
    syncToParent();
    onGoToPage?.(page);
  };
  const formIsValid = () => {
    const newErrors = {
      companyVisionAndMission: !companyVisionAndMission?.trim()
        ? "Company Mission And Vission is required"
        : "",
      employees:
        employees === "Please select employees" || !employees?.trim()
          ? "Employee is required"
          : "",
      industry:
        industry === "Please select industry" || !industry?.trim()
          ? "Industry is required"
          : "",
      services:
        services === "Please select services" || !services?.trim()
          ? "Service is required"
          : "",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  useEffect(() => {
    if (initialData) {
      setCompanyVisionAndMission(initialData?.companyVisionAndMission);
      setEmployees(initialData?.employees || "Please select employees");
      setIndustry(initialData?.industry || "Please select industry");
      setServices(initialData?.services || "Please select services");
    }
  }, []);

  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) return;

    const nextData = { ...initialData, ...buildStepData() };
    const hasChanges =
      JSON.stringify(nextData) !== JSON.stringify(initialData);

    if (hasChanges) {
      setProfileData(nextData);
    }
  }, [
    companyVisionAndMission,
    employees,
    industry,
    services,
    initialData,
    setProfileData,
  ]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (!formIsValid()) return;

    syncToParent();
    onNext();
  };

  const industryList = industryServiceData?.map((item) => item.industry);
  const selectedIndustryServices =
    industryServiceData.find((item) => item.industry === industry)?.services ||
    [];

  return (
    <div>
      <Container
        maxWidth="md"
        style={{
          display: "flex",
          padding: "20px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          spacing={2}
          bgcolor="#FFFFFF"
          width="784px"
          borderRadius="22px"
          border="1px solid #D6D6D7"
          margin={0}
        >
          <Grid
            item
            height={isMobile ? "auto" : "auto"}
            style={{ padding: 30 }}
          >
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap="10px"
            >
              <Grid item display="flex" alignItems="center" gap="10px">
                <ArrowBackIcon
                  onClick={handleBack}
                  style={{ cursor: "pointer" }}
                />
                <Typography color="#273848" fontWeight={700} fontSize="20.94px">
                  {id ? "Update Profile" : "Create Profile"}
                </Typography>
              </Grid>
              <ProfileStepDots
                currentPage={currentPage}
                onGoToPage={handleStepNavigate}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Company Mission and Vision
              </Typography>
              <Grid mb={1}></Grid>
              <OutlinedInput
                placeholder="Type here..."
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={companyVisionAndMission}
                onChange={(e) => {
                  const value = e.target.value;
                  setCompanyVisionAndMission(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      companyVisionAndMission: "",
                    }));
                  }
                }}
              />
            </Grid>
            <InputLabel
              style={{
                color: "#f44336",
                paddingTop: "3px",
                fontSize: "12px",
                paddingLeft: "10px",
              }}
            >
              {errors.companyVisionAndMission}
            </InputLabel>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">Employees</Typography>
              <Grid mb={1}></Grid>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={employees}
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  setEmployees(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      employees: "",
                    }));
                  }
                }}
                displayEmpty
                renderValue={(value) => (
                  <span className={value === "" ? "placeholder" : ""}>
                    {value === "" ? "Select number of employees" : value}
                  </span>
                )}
              >
                <MenuItem value="" disabled>
                  Select number of employees
                </MenuItem>
                <MenuItem value={"0-10"}>0-10</MenuItem>
                <MenuItem value={"10-50"}>10-50</MenuItem>
                <MenuItem value={"50-200"}>50-200</MenuItem>
                <MenuItem value={"200-1000"}>200-1000</MenuItem>
              </Select>
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.employees}
              </InputLabel>
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">Industry</Typography>
              <Grid mb={1}></Grid>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={industry}
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  setIndustry(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      industry: "",
                    }));
                  }
                }}
                displayEmpty
                renderValue={(value) => (
                  <span className={value === "" ? "placeholder" : ""}>
                    {value === "" ? "Select industry" : value}
                  </span>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select industry
                </MenuItem>
                {industryList.map((industryOption) => (
                  <MenuItem key={industryOption} value={industryOption}>
                    {industryOption}
                  </MenuItem>
                ))}
              </Select>
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.industry}
              </InputLabel>
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">Services</Typography>
              <Grid mb={1}></Grid>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={services}
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  setServices(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      services: "",
                    }));
                  }
                }}
                displayEmpty
                renderValue={(value) => (
                  <span className={value === "" ? "placeholder" : ""}>
                    {value === "" ? "Select services" : value}
                  </span>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select services
                </MenuItem>
                {selectedIndustryServices?.map((service) => (
                  <MenuItem key={service} value={service}>
                    {service}
                  </MenuItem>
                ))}
              </Select>
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.services}
              </InputLabel>
            </Grid>
            <Grid mt="155px"></Grid>
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid xs={2.5}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #000000",
                    color: "#273848",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                  }}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Grid>
              <Grid
                item
                xs={isMobile ? 0 : 3}
                style={{ maxWidth: "fitContent" }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#84E1CC",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                  }}
                  onClick={(event) => handleSubmit(event)}
                >
                  Continue
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default ProfileSecond;
